import { GripVerticalIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OrderingItem } from "@/types/hld-wizard";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface DraggableListProps {
  items: OrderingItem[];
  order: string[];
  onReorder: (newOrder: string[]) => void;
  disabled?: boolean;
}

export function DraggableList({
  items,
  order,
  onReorder,
  disabled,
}: DraggableListProps) {
  const itemMap = new Map(items.map((item) => [item.id, item]));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = order.indexOf(String(active.id));
      const newIndex = order.indexOf(String(over.id));
      onReorder(arrayMove(order, oldIndex, newIndex));
    }
  }

  function moveItem(from: number, to: number) {
    if (to < 0 || to >= order.length) return;
    onReorder(arrayMove([...order], from, to));
  }

  if (disabled) {
    return (
      <div className="space-y-1.5">
        {order.map((id, i) => {
          const item = itemMap.get(id);
          if (!item) return null;
          return (
            <div
              key={id}
              className="flex items-center gap-3 rounded-lg border p-3 opacity-60"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-xs font-semibold">
                {i + 1}
              </span>
              <span className="flex-1 text-sm">{item.text}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5">
          {order.map((id, i) => {
            const item = itemMap.get(id);
            if (!item) return null;
            return (
              <SortableItem
                key={id}
                id={id}
                index={i}
                text={item.text}
                totalItems={order.length}
                onMoveUp={() => moveItem(i, i - 1)}
                onMoveDown={() => moveItem(i, i + 1)}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({
  id,
  index,
  text,
  totalItems,
  onMoveUp,
  onMoveDown,
}: {
  id: string;
  index: number;
  text: string;
  totalItems: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded-lg border bg-background p-3 transition-shadow",
        isDragging && "z-10 shadow-lg border-primary/30",
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVerticalIcon className="h-4 w-4 shrink-0 text-muted-foreground/50" />
      </button>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-xs font-semibold">
        {index + 1}
      </span>
      <span className="flex-1 text-sm">{text}</span>
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0}
          className="rounded px-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
          aria-label="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === totalItems - 1}
          className="rounded px-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
          aria-label="Move down"
        >
          ↓
        </button>
      </div>
    </div>
  );
}
